<template>
  <div
    class="rounded"
    :key="submission.id"
    :class="[
      fromGenieRecap && isGenieRecap
        ? 'bg-blueGray-100 border border-solid border-blueGray-200'
        : 'bg-gray-100 border border-solid border-gray-200',
    ]"
  >
    <template v-if="!showModelSubmission && !studentInHardClosedRT">
      <div v-if="fromGenieRecap && isGenieRecap" class="px-10 pt-7 w-full">
        <div v-if="genieRecapQuestionInCurrentRT" class="mb-6">
          <div class="mb-3.5">
            <h6 class="font-bold text-sm mb-1">
              Follow Up Questions ({{ genieRecap.questions.length }})
            </h6>
            <span class="text-blueGray-500 font-light font-poppins text-sm"
              >Leave a comment to respond to these follow up questions
              below!</span
            >
          </div>
          <div v-for="(q, index) in genieRecap.questions" :key="index">
            <span class="flex items-center leading-7 text-gray-700 text-sm">
              {{ index + 1 }}. {{ q.question }}
            </span>
          </div>
        </div>
      </div>
      <div v-else class="px-10 pt-7 w-full">
        <div
          v-if="peer_guidance_questions_to_show.length"
          @click="
            collapsed_peer_guidance_questions =
              !collapsed_peer_guidance_questions
          "
          class="flex items-center mb-3"
        >
          <h6 class="font-bold text-sm">
            Feedback Questions ({{ peer_guidance_questions_to_show.length }})
            <i
              v-if="collapsed_peer_guidance_questions"
              class="fas fa-arrow-down fa-sm ml-2"
            ></i
            ><i v-else class="fas fa-arrow-up fa-sm ml-2"></i>
          </h6>
        </div>
        <div v-show="peer_guidance_questions_to_show.length && !collapsed_peer_guidance_questions" class="mb-6">
          <span
            v-for="(question, index) in peer_guidance_questions_to_show"
            :key="index"
            class="flex items-center leading-7 text-gray-700 hover:text-primary text-sm"
            @click="selectAutomatedFeedback(question)"
          >
            {{ question }}
          </span>
        </div>
      </div>
      <div class="w-full flex justify-between px-10">
        <div
          class="w-full h-full bg-white rounded focus-within:border-primary border border-coolGray-100 mb-8"
        >
          <textarea
           
            v-model="comment_content"
            v-if="!is_report_page"
            class="w-full outline-none h-32 px-4 rounded py-4"
            placeholder="Leave a comment"
          ></textarea>
          <div
            class="flex relative justify-between items-center border-t border-coolGray-200"
          >
            <EmojiPicker
              :search="searchEmoji"
              @emoji="append"
              class="emoji-container ml-3"
            >
              <div
                class="emoji-invoker"
                slot="emoji-invoker"
                slot-scope="{ events: { click: isMenuEmojisOpen } }"
              >
                <button
                  id="openEmojiContainer"
                  class="w-8 h-8 hover:bg-coolGray-200 rounded-full"
                  @click="isMenuEmojisOpen"
                >
                  <i class="fa-solid text-base text-gray-500 fa-face-grin"></i>
                </button>
              </div>
              <div slot="emoji-picker" slot-scope="{ emojis, insert }">
                <div
                  class="emoji-picker absolute w-80 h-56 px-5 shadow rounded border border-coolGray-300 py-4 z-50 overflow-x-hidden overflow-y-auto bg-white"
                >
                  <input
                    placeholder="Search"
                    type="text"
                    v-model="searchEmoji"
                    class="py-2 px-4 rounded w-full border-2 mb-3"
                  />
                  <div>
                    <div
                      v-for="(emojiGroup, category) in emojis"
                      :key="category"
                    >
                      <h5>{{ category }}</h5>
                      <div class="emojis">
                        <span
                          v-for="(emoji, emojiName) in emojiGroup"
                          :key="emojiName"
                          @click="insert(emoji)"
                          :title="emojiName"
                        >
                          {{ emoji }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </EmojiPicker>
            <button
              class="sample py-2 my-3 mr-3 w-28 bg-primary rounded cursor-pointer text-white font-semibold disabled:opacity-50"
              @click="postComment"
              :disabled="submitting"
            >
              Send
            </button>
          </div>
        </div>
      </div>
    </template>
    <AddModelSubmission
      v-if="
        (showModelSubmission && canAddModelSubmission) || editModelSubmission
      "
      ref="model-submission-textbox"
      id="model-submission-in-comment"
      :modelID="model_id"
      :modelContent="model_content"
      :focusOnMounted="true"
      :submissionId="`${submission.id}`"
      @closeModelSubmission="closeModelSubmission"
    />
    <CommentCardList
      v-show="model_submission && showComments && !editModelSubmission"
      key="model"
      :listOfComments="model_submission"
      :submission="submission"
      :submissionID="submission.id"
      :isInSummary="isInSummary"
      class="transition delay-150 duration-300 ease-in-out"
    />
    <CommentCardList
      v-show="
        !getRTLoading.full_submission.includes(submission.id) && showComments
      "
      key="comments"
      :listOfComments="ordered_submission_comments"
      :submission="submission"
      :submissionID="submission.id"
      class="mt-3 transition delay-150 duration-300 ease-in-out"
    />
    <CommentCardList
      v-if="fromGenieRecap && isGenieRecap"
      key="recap-comments"
      :listOfComments="recapComments"
      :fromGenieRecap="true"
      class="mt-3 transition delay-150 duration-300 ease-in-out"
    />
  </div>
</template>

<script>
  import Vue from 'vue';
  import { mapGetters } from 'vuex';
  import sortBy from 'lodash/sortBy';
  import { EventBus } from '../../../utils/event-bus';
  import courseSocketMixin from 'mixins/sockets/courseSocketEvents';

  Vue.directive('clickOutside', {
    bind: function (el, binding, vnode) {
      el.clickOutsideEvent = function (event) {
        if (!(el == event.target || el.contains(event.target)))
          vnode.context[binding.expression](event);
      };
      window.addEventListener('click', el.clickOutsideEvent);
    },
    unbind: function (el) {
      window.removeEventListener('click', el.clickOutsideEvent);
    },
  });

  export default Vue.extend({
    data() {
      return {
        comment_content: '',
        submitting_comment_message: '',
        editModelSubmission: false,
        model_id: '',
        model_content: '',
        timeOutId: null,
        collapsed_peer_guidance_questions: false,
        searchEmoji: '',
        isMenuEmojisOpen: false,
        commentToScroll: null,
        submitting: false,
      };
    },
    components: {
      EmojiPicker: () => import('vue-emoji-picker'),
      CommentCardList: () => import('./commentCardList'),
      Textbox: () => import('components/textbox.vue'),
      AddModelSubmission: () =>
        import('./addModelSubmission/addModelSubmission'),
    },
    mixins: [courseSocketMixin],
    props: {
      submission: {
        type: Object,
        default() {
          return {};
        },
      },
      showComments: {
        type: Boolean,
        default: false,
      },
      showModelSubmission: {
        type: Boolean,
        default: false,
      },
      isInSummary: {
        type: Boolean,
        default: false,
      },
      isFromNotification: {
        type: Boolean,
        default: false,
      },
      isFromNotificationCommentId: {
        type: String,
        default: '',
      },
      fromGenieRecap: {
        type: Boolean,
        default: false,
      },
      recapCommentsFromService: {
        type: Array,
        default() {
          return [];
        },
      },
    },
    computed: {
      ...mapGetters([
        'getCurrentRT',
        'getRTLoading',
        'studentInHardClosedRT',
        'getStudentSummary',
        'getProfile',
      ]),
      is_report_page() {
        return this.$route.name === 'course.student-summary';
      },
      peer_guidance_questions_to_show() {
        const peer_assessment_questions = this.is_report_page ? this.getStudentSummary.submission.roundtable.peer_assessment_questions : this.getCurrentRT.peer_assessment_questions;
        return peer_assessment_questions.filter((el) => el.is_active).map((el) => el.question);
      },
      loading_comments() {
        return this.getRTLoading.full_submission.includes(this.submission.id);
      },
      canAddModelSubmission() {
        return !this.submission.is_model_response;
      },
      ordered_submission_comments() {
        if (this.submission && this.submission.comments) {
          const comments = this.submission.comments.filter(
            (comment) => !comment.is_model_submission
          );
          const sortComments = () =>
            sortBy(comments, [
              (comment) => new Date() - new Date(comment.created_at),
            ]);
          // return this.submission.comments ? sortComments() : this.temporary_comment_cache;
          return this.submission.comments ? sortComments() : [];
        }
      },
      model_submission() {
        if (this.submission && this.submission.comments) {
          return this.submission.comments.filter(
            (comment) => comment.is_model_submission
          );
        }
      },
      isFromNotificationState() {
        return this.$store.state.isFromNotification;
      },
      genieRecap() {
        return this.getCurrentRT.genie_recap;
      },
      isGenieRecap() {
        return this.genieRecap.id ? true : false;
      },
      genieRecapQuestionInCurrentRT() {
        return this.genieRecap.is_current_rt;
      },
      recapComments() {
        return this.genieRecap.comments
          ? this.genieRecap.comments
          : this.recapCommentsFromService;
      },
    },
    methods: {
      append(emoji) {
        this.comment_content += emoji;
      },
      clickEmojis: function () {},
      secondClick: function () {
        document.getElementById('openEmojiContainer').classList.add('show');
      },
      isMenuEmojisOpened: function () {
        /*  */
      },
      clickClose: function () {
        document.getElementById('openEmojiContainer').click();
      },
      commentScrollElement: function () {
        const CommentElement = document.getElementById(
          `individual-comment-${this.$route.query.topLevelCommentID}`
        );
        return CommentElement;
      },

      postComment: async function () {
        if (this.comment_content.trim() === '') return;
        this.submitting = true;

        /* Send email to user if it not online in course */
        if (this.fromGenieRecap && this.isGenieRecap) {
          await this.$store
            .dispatch('postRecapComment', {
              user_id: this.getProfile.id,
              roundtable_id: this.$route.params.roundtableID,
              recap_id: this.genieRecap.id,
              content: this.comment_content,
              is_model_submission: false,
              is_to_send_email: false,
              type: 'comment',
            })
            .finally(() => (this.submitting = false));
        } else {
          const members_channel =
            window.pusher.channels.channels[
              `presence-course-${this.$route.params.courseID}`
            ];
          const submissions = this.getCurrentRT.submissions;
          const user_on_post_comment = submissions.filter(
            (submission) => submission.id === this.submission.id
          );
          let is_user_on_written;
          const user = members_channel.members.get(
            user_on_post_comment[0].user_id
          );
          members_channel.members.each(function (member) {
            if (member.id === user_on_post_comment[0].user_id) {
              is_user_on_written = true;
              return;
            }
            is_user_on_written = false;
          });
          await this.$store
            .dispatch('postComment', {
              content: this.comment_content,
              submission_id: this.submission.id,
              is_model_submission: false,
              is_to_send_email:
                user === null || is_user_on_written === false ? true : false,
              type: 'comment',
            })
            .finally(() => (this.submitting = false));
        }
        if (!this.submission.comments) {
          this.$emit('getComments');
        } else if (!this.showComments) {
          this.$emit('toggleSubmissionComments');
        }
        this.comment_content = '';
        /* let submission = Object.assign({}, this.submission);
        submission.comment_content = ''; */
        
        this.submitting_comment_message = '';
        if (this.fromGenieRecap === true) return;
        /* await this.$store.commit('UPDATE_SUBMISSION_SPREAD', submission); */
      },
      updateModelSubmission(model, flag) {
        if (model && flag) {
          if (model.submission_id === this.submission.id) {
            this.editModelSubmission = true;
            this.model_id = model.id;
            this.model_content = model.content;
          }
        } else {
          this.model_id = '';
          this.model_content = '';
        }
      },
      closeModelSubmission() {
        this.$emit('closeModelSubmission', this.submission.id);
      },
      saveChanges() {
        if (this.fromGenieRecap === true) return;
        /* let submission = Object.assign({}, this.submission);
          submission.comment_content = this.comment_content;
          this.$store.commit('UPDATE_SUBMISSION_SPREAD', submission); */
        /* clearTimeout(this.timeOutId);
        this.timeOutId = setTimeout(() => {
          if (this.submitting_comment_message.length) return;
          // Runs 1 second (1000 ms) after the last change
          let submission = Object.assign({}, this.submission);
          submission.comment_content = this.comment_content;
          this.$store.commit('UPDATE_SUBMISSION_SPREAD', submission);
        }, 1000); */
      },
      selectAutomatedFeedback(question) {
        if (this.comment_content && this.comment_content.length) {
          this.comment_content += `\n${question}`;
          /* let submission = Object.assign({}, this.submission);
          submission.comment_content = this.comment_content;
          this.$store.commit('UPDATE_SUBMISSION_SPREAD', submission); */
        } else {
          this.comment_content = question;
          /* let submission = Object.assign({}, this.submission);
          submission.comment_content = this.comment_content;
          this.$store.commit('UPDATE_SUBMISSION_SPREAD', submission);this.saveChanges(); */
        }
        
      },
    },
    mounted: async function () {
      EventBus.$on('edit-model-submission', (model, flag) => {
        if (model && flag) {
          if (model.submission_id === this.submission.id) {
            this.editModelSubmission = true;
            this.model_id = model.id;
            this.model_content = model.content;
          }
        } else {
          this.model_id = '';
          this.model_content = '';
        }
      });
      EventBus.$on('close-model-submission', () => {
        this.editModelSubmission = false;
        this.model_id = '';
        this.model_submission_content = '';
        this.$emit('closeModelSubmission');
      });
      /* if (this.submission.comment_content) {
        this.comment_content = this.submission.comment_content;
      } */
      const waitForElm = (selector) => {
        return new Promise((resolve) => {
          if (document.getElementById(selector)) {
            return resolve(document.getElementById(selector));
          }
          const observer = new MutationObserver(() => {
            if (document.getElementById(selector)) {
              resolve(document.getElementById(selector));
              observer.disconnect();
            }
          });
          observer.observe(document.body, { childList: true, subtree: true });
        });
      };
      waitForElm(
        `individual-comment-${this.$route.query.topLevelCommentID}`
      ).then((comment_element) => {
        if (comment_element) {
          setTimeout(() => {
            comment_element.scrollIntoView({
              block: 'start',
              behavior: 'smooth',
              inline: 'start',
            });
          }, 1000);
        }
      });
      if (
        this.$route.query.from_email === 'reply' ||
        this.$route.query.recapID
      ) {
        waitForElm(`reply-comment-${this.$route.query.replyCommentID}`).then(
          (reply_element) => {
            if (reply_element) {
              setTimeout(() => {
                reply_element.scrollIntoView({
                  block: 'start',
                  behavior: 'smooth',
                  inline: 'start',
                });
              }, 1000);
            }
          }
        );
      }
    },
    beforeDestroy() {
      EventBus.$off('edit-model-submission');
      EventBus.$off('close-model-submission');
    },
  });
</script>
<!-- CSS -->
<style scoped lang="stylus">

  button.see-comments-btn
    background-color: white
    box-shadow: 0px 0px 2px 1px rgba( 0, 0, 0, 0.2 )
    &:hover
      background-color: pl-secondary
      color: white;

  .hide-arrow
    max-width: 24px
  .peer-assessment-questions
    padding-bottom: 10px
    span
      line-height: 2
  .sticky-button-row
    background-color: #fff
    border: 1px solid pl-fog
    bottom: 0px
    pointer-events: none
</style>



// WEBPACK FOOTER //
// commentsTab.vue?ee39334e